/**
 * Build-Your-Own Bundle (Client-Side, Oct 2025)
 * Shopify Dawn compatible
 *
 * README (what this does):
 * - Loads ALL products from the 'bundle-eligible' collection using Shopify's JSON endpoint:
 *     /collections/bundle-eligible/products.json?limit=250&page=N
 *   (We page through until <250 results returned.)
 * - Caches the full dataset in localStorage (keyed + TTL) to avoid re-fetching within the TTL.
 * - Auto-detects orgs from product tags starting with "org:" (slug-ified), builds the dropdown:
 *     "All", "Non-Branded", and one option per org found.
 * - Renders product cards client-side with:
 *     image (width/height attributes for CLS), title, variant <select> (even for single variant),
 *     price (updates on variant change), and quantity controls (+/–).
 * - Live filters (org + search) apply instantly without reload.
 * - Sticky summary shows subtotal and savings tiers (2=5%, 3–4=10%, 5+=15%).
 * - Checkout posts all chosen variants via /cart/add.js then, if eligible and flagged, redirects through:
 *     /discount/BUNDLE5|10|15?redirect=/cart
 * - Never double-initializes (global guard).
 *
 * Console Diagnostics:
 *   [Bundle] DATA size: <n>
 *   [Bundle] Filtering by org: <slug> search: "<q>"
 *   [Bundle] After org filter: <n>
 *   [Bundle] Final products to render: <n>
 *
 * Configuration (edit if needed):
 *   - COLLECTION_HANDLE: change if your source collection handle differs.
 *   - CACHE_TTL_MIN: how long to keep dataset cached.
 *   - FETCH_CONCURRENCY: page fetch concurrency (keep modest for Shopify).
 */

(() => {
  'use strict';
  if (window.__GR_BUNDLER__) return;        // guard: prevent double init
  window.__GR_BUNDLER__ = true;

  const root = document.getElementById('gr-bundle-root');
  if (!root) return;

  // Elements
  const grid           = document.getElementById('gr-grid');
  const orgSelect      = document.getElementById('gr-org');
  const searchInput    = document.getElementById('gr-search');
  const subtotalEl     = document.getElementById('gr-subtotal');
  const savingsEl      = document.getElementById('gr-savings');
  const checkoutBtn    = document.getElementById('gr-checkout');

  // Config
  const COLLECTION_HANDLE   = grid?.dataset?.collectionHandle || 'bundle-eligible';
  const KNOWN_TOTAL         = Number(grid?.dataset?.total || 0);
  const LIMIT_PER_PAGE      = 250;
  const CACHE_KEY           = 'gr_bundle_dataset_v3';
  const CACHE_TS_KEY        = 'gr_bundle_dataset_ts_v3';
  const CACHE_TTL_MIN       = 120; // cache data for 2 hours
  const FETCH_CONCURRENCY   = 5;
  const DISCOUNT_FLAG_KEY   = 'gr_bundle_active';
  const DISCOUNT_FLAG_TTL   = 120; // minutes
  const BUNDLE_STATE_KEY     = 'gr_bundle_state_v1';
  const BUNDLE_STATE_TTL_MIN = 240; // 4 hours (adjust if needed)

  // State
  let DATA = [];                       // full product dataset from collection
  let FILTERED = [];                   // current filtered set
  const bundle = Object.create(null);  // variantId -> { price:Number, quantity:Number, title:String }

  // Utilities
  const now = () => Date.now();
  const fmt = n => `$${(Number(n)||0).toFixed(2)}`;

  // 2 items = 5%, 3–4 items = 10%, 5+ items = 15%
const tier = qty => (qty >= 5 ? 0.15 : qty >= 3 ? 0.10 : qty >= 2 ? 0.05 : 0);

  const setDiscountFlag = () => {
    try { localStorage.setItem(DISCOUNT_FLAG_KEY, JSON.stringify({active:true, ts: now()})); } catch {}
  };
  const hasDiscountFlag = () => {
    try {
      const raw = localStorage.getItem(DISCOUNT_FLAG_KEY); if (!raw) return false;
      const {active, ts} = JSON.parse(raw) || {};
      if (!active || !ts) return false;
      return ((now()-ts)/60000) < DISCOUNT_FLAG_TTL;
    } catch { return false; }
  };

  const slugify = s => String(s||'').toLowerCase()
    .replace(/&/g,'and')
    .replace(/[^a-z0-9]+/g,'-')
    .replace(/(^-|-$)/g,'');

  const titleCaseFromSlug = slug => slug
    .split('-')
    .map(w => w ? w[0].toUpperCase()+w.slice(1) : '')
    .join(' ');

  // Safe decode (?org= may be double-encoded like Kappa%2520Delta)
  function multiDecode(s) {
    let out = s || '';
    try {
      out = decodeURIComponent(out);
      if (/%[0-9A-Fa-f]{2}/.test(out)) out = decodeURIComponent(out);
    } catch {}
    return out;
  }

  // === NEW: Inject minimal CSS so Add sits inline and has rounded corners ===
  function injectBundlerStyles() {
    const STYLE_ID = 'gr-bundler-inline-controls-css';
    if (document.getElementById(STYLE_ID)) return;

    const css = `
      /* === Bundle controls row: keep stepper + Add on one line === */
      .gr-controls-row{
        display:flex;
        align-items:center;
        justify-content:center;
        gap:12px;
        margin-top:12px;
        flex-wrap:nowrap;
      }

      /* Quantity stepper */
      .gr-qty{
        display:flex;
        align-items:center;
        gap:8px;
        flex:0 0 auto;
      }

      /* Normalize control heights + rounding */
      .gr-qty .gr-minus,
      .gr-qty .gr-plus,
      .gr-qty .gr-count,
      .gr-add-inline{
        height:42px;
        border-radius:12px; /* rounder corners */
      }

      /* Stepper buttons */
      .gr-qty .gr-minus,
      .gr-qty .gr-plus{
        width:42px;
        border:1px solid #D1D5DB;
        background:#fff;
        font-size:18px;
        font-weight:600;
        line-height:1;
        cursor:pointer;
      }

      /* Quantity input */
      .gr-qty .gr-count{
        width:64px;
        text-align:center;
        border:1px solid #D1D5DB;
        font-weight:500;
      }

      /* Add button inline */
      .gr-add-inline{
        padding:0 14px;
        border:1px solid #111827;
        background:#fff;
        color:#111827;
        font-weight:600;
        cursor:pointer;
        white-space:nowrap;
        flex:0 0 auto;
      }
      .gr-add-inline:disabled{
        opacity:.45;
        cursor:not-allowed;
      }
      .gr-add-inline:not(:disabled):hover{
        background:#F9FAFB;
      }
      .gr-add-inline:not(:disabled):active{
        transform:translateY(1px);
      }
    `;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = css;
    document.head.appendChild(style);
  }

  // Data fetch w/ retry
  async function fetchJSON(url, retries=2) {
    for (let attempt=0; attempt<=retries; attempt++) {
      try {
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return await res.json();
      } catch (e) {
        if (attempt === retries) throw e;
        await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt))); // 500ms, 1s
      }
    }
  }

  async function loadAllPages() {
    const all = [];
    let page = 1;
    let keepGoing = true;

    while (keepGoing) {
      const batch = [];
      for (let i=0; i<FETCH_CONCURRENCY; i++) {
        const url = `/collections/${COLLECTION_HANDLE}/products.json?limit=${LIMIT_PER_PAGE}&page=${page+i}`;
        batch.push(fetchJSON(url).catch(() => ({products: []})));
      }
      const results = await Promise.all(batch);
      let batchHadShort = false;
      for (const r of results) {
        const arr = Array.isArray(r?.products) ? r.products : [];
        all.push(...arr);
        if (arr.length < LIMIT_PER_PAGE) batchHadShort = true;
      }
      page += FETCH_CONCURRENCY;
      if (batchHadShort) keepGoing = false;

      if (KNOWN_TOTAL && all.length >= KNOWN_TOTAL) break;
    }
    return all;
  }

  function saveCache(list) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(list));
      localStorage.setItem(CACHE_TS_KEY, String(now()));
    } catch {}
  }
  function loadCache() {
    try {
      const dataStr = localStorage.getItem(CACHE_KEY);
      const tsStr   = localStorage.getItem(CACHE_TS_KEY);
      if (!dataStr || !tsStr) return null;
      const ageMin = (now()-Number(tsStr))/60000;
      if (ageMin > CACHE_TTL_MIN) return null;
      const parsed = JSON.parse(dataStr);
      if (!Array.isArray(parsed) || !parsed.length) return null;
      return parsed;
    } catch { return null; }
  }

  function saveBundleState() {
    try {
      const payload = { ts: now(), bundle };
      localStorage.setItem(BUNDLE_STATE_KEY, JSON.stringify(payload));
    } catch {}
  }

  function loadBundleState() {
    try {
      const raw = localStorage.getItem(BUNDLE_STATE_KEY);
      if (!raw) return null;
      const { ts, bundle: saved } = JSON.parse(raw) || {};
      if (!ts || !saved) return null;
      const ageMin = (now() - ts) / 60000;
      if (ageMin > BUNDLE_STATE_TTL_MIN) return null;
      return saved;
    } catch { return null; }
  }

  // Price normalization (Shopify products.json variants price is often in cents as Number or String)
  function priceToDollars(raw) {
    const n = Number(raw);
    if (!isFinite(n)) return 0;
    return n >= 1000 ? n/100 : (n > 0 && n%1===0 ? n/100 : n); // if big integer assume cents
  }

  // Image helpers
  function primaryImage(product) {
    let src = '';
    let w = 0, h = 0;
    if (Array.isArray(product.images) && product.images.length) {
      const img = product.images[0];
      if (img) {
        src = typeof img === 'string' ? img : (img.src || '');
        w = Number(img.width || 0);
        h = Number(img.height || 0);
      }
    } else if (product.image) {
      src = typeof product.image === 'string' ? product.image : (product.image?.src || '');
      w = Number(product.image?.width || 0);
      h = Number(product.image?.height || 0);
    }
    if (!src) {
      src = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
      w = 800; h = 500;
    }
    if (typeof src === 'string' && src && /cdn\.shopify\.com/.test(src) && !/[?&]width=\d+/.test(src)) {
      const sep = src.includes('?') ? '&' : '?';
      src = `${src}${sep}width=800`;
      if (!w || !h) { w = 800; h = 500; }
    }
    if (!w || !h) { w = 800; h = 500; }
    return { src, width:w, height:h };
  }

  function renderCard(p) {
    const { src, width, height } = primaryImage(p);
    const variants = Array.isArray(p.variants) ? p.variants : [];
    const initial  = variants.find(v => v.available) || variants[0] || null;
    const initialId = initial ? String(initial.id) : '';
    const initialPrice = initial ? priceToDollars(initial.price) : 0;

    const card = document.createElement('div');
    card.className = 'gr-card';
    card.setAttribute('data-handle', p.handle || '');

    const productURL = p.url || `/products/${p.handle}`;

    card.innerHTML = `
      <a class="gr-link" href="${productURL}">
        <div class="gr-imgwrap" style="padding-top:${Math.round((height/width)*10000)/100}%">
          <img src="${src}" alt="${(p.title||'').replace(/"/g,'&quot;')}" width="${width}" height="${height}" loading="lazy" />
        </div>
        <h3 class="gr-title-sm">${p.title||''}</h3>
      </a>

      <div class="gr-variant">
        <select class="gr-variant-select" aria-label="Select variant">
          ${
            variants.length
              ? variants.map(v => {
                  const id = String(v.id);
                  const price = priceToDollars(v.price);
                  const disabled = v.available ? '' : 'disabled';
                  const sel = id === initialId ? 'selected' : '';
                  return `<option value="${id}" data-price="${price}" ${disabled} ${sel}>${v.title}${v.available?'':' (Sold out)'}</option>`;
                }).join('')
              : `<option value="" disabled selected>No variants</option>`
          }
        </select>
      </div>

      <p class="gr-price">${fmt(initialPrice)}</p>

      <div class="gr-controls-row">
        <div class="gr-qty" data-variant-id="${initialId}">
          <button class="gr-minus" type="button" aria-label="Decrease quantity">−</button>
          <input class="gr-count" type="number" min="0" step="1" value="0" inputmode="numeric" />
          <button class="gr-plus" type="button" aria-label="Increase quantity">+</button>
        </div>

        <button class="gr-add-inline" type="button" disabled aria-label="Add this item to your bundle">Add</button>
      </div>

      <div class="gr-added" aria-live="polite"></div>
    `;

    const priceEl = card.querySelector('.gr-price');
    const picker  = card.querySelector('.gr-variant-select');
    const qtyWrap = card.querySelector('.gr-qty');
    const input   = card.querySelector('.gr-count');
    const plus    = card.querySelector('.gr-plus');
    const minus   = card.querySelector('.gr-minus');
    const addBtn  = card.querySelector('.gr-add-inline');
    const added   = card.querySelector('.gr-added');

    function clampQty(val) {
      return Math.max(0, Math.floor(Number(val) || 0));
    }

    function currentVariantId() {
      return qtyWrap.getAttribute('data-variant-id') || '';
    }

    function currentVariant() {
      const vid = currentVariantId();
      return variants.find(x => String(x.id) === vid) || null;
    }

    function flash(msg) {
      added.textContent = msg || '';
      if (!msg) return;
      setTimeout(() => { if (added.textContent === msg) added.textContent = ''; }, 1200);
    }

    function isCommitted(variantId) {
      return !!(bundle[variantId] && bundle[variantId].quantity > 0);
    }

    function updateAddButtonState() {
      const vid = currentVariantId();
      const q = clampQty(input.value);
      const committed = isCommitted(vid);

      addBtn.disabled = (q === 0 && !committed);

      if (committed && q === 0) addBtn.textContent = 'Remove';
      else if (committed && q > 0) addBtn.textContent = 'Update';
      else addBtn.textContent = 'Add';
    }

    function syncInputFromBundle() {
      const vid = currentVariantId();
      const committedQty = bundle[vid]?.quantity || 0;
      input.value = String(committedQty);
      updateAddButtonState();
    }

    syncInputFromBundle();

    if (picker) {
      picker.addEventListener('change', (e) => {
        const opt = e.target.selectedOptions[0];
        const newId = String(opt.value || '');
        const newPrice = Number(opt.dataset.price || 0);

        qtyWrap.setAttribute('data-variant-id', newId);
        priceEl.textContent = fmt(newPrice);

        if (opt.hasAttribute('disabled')) {
          input.value = '0';
          updateAddButtonState();
          flash('Sold out.');
          return;
        }

        syncInputFromBundle();
        flash('');
      });
    }

    plus?.addEventListener('click', () => {
      input.value = String(clampQty(input.value) + 1);
      updateAddButtonState();
    });

    minus?.addEventListener('click', () => {
      const curr = clampQty(input.value);
      input.value = String(Math.max(0, curr - 1));
      updateAddButtonState();
    });

    input?.addEventListener('input', () => updateAddButtonState());
    input?.addEventListener('change', () => {
      input.value = String(clampQty(input.value));
      updateAddButtonState();
    });

    addBtn?.addEventListener('click', () => {
      const vid = currentVariantId();
      if (!vid) return;

      const v = currentVariant();
      if (!v || v.available === false) {
        input.value = '0';
        updateAddButtonState();
        flash('Sold out.');
        return;
      }

      const q = clampQty(input.value);
      const price = priceToDollars(v.price);

      if (q > 0) {
        bundle[vid] = {
          price,
          quantity: q,
          productTitle: p.title || '',
          variantTitle: v?.title || '',
          title: p.title || ''
        };
        setDiscountFlag();
        saveBundleState();
        updateSummaryFromBundle();
        flash(isCommitted(vid) ? 'Updated.' : 'Added.');
      } else {
        if (bundle[vid]) {
          delete bundle[vid];
          saveBundleState();
          updateSummaryFromBundle();
        }
        flash('Removed.');
      }

      updateAddButtonState();
    });

    return card;
  }

  function render(list) {
    grid.innerHTML = '';
    const frag = document.createDocumentFragment();
    list.forEach(p => frag.appendChild(renderCard(p)));
    grid.appendChild(frag);
  }

  function detectOrgs(products) {
    const slugs = new Set();

    products.forEach(p => {
      (p.tags || []).forEach(t => {
        const tag = String(t).trim();

        const lower = tag.toLowerCase();
        if (
          lower.includes('bundle') ||
          lower.includes('mat') ||
          lower.includes('rug') ||
          lower.includes('welcome')
        ) return;

        slugs.add(slugify(tag));
      });
    });

    return Array.from(slugs).sort();
  }

  function productMatchesOrg(p, slug, allOrgSlugs) {
    if (!slug) return true;
    if (slug === '__non_branded__') {
      const hasOrgTag = (p.tags||[]).some(t => /^org\s*:/i.test(t));
      const titleTags = `${p.title||''} ${(p.tags||[]).join(' ')}`.toLowerCase();
      const anyName = allOrgSlugs.some(s => titleTags.includes(titleCaseFromSlug(s).toLowerCase()));
      return !hasOrgTag && !anyName;
    }
    const tagHit = (p.tags||[]).some(t => {
      const m = String(t).match(/^org\s*:\s*(.+)$/i);
      return m && slugify(m[1]) === slug;
    });
    if (tagHit) return true;

    const label = titleCaseFromSlug(slug);
    const hay = `${p.title||''} ${(p.tags||[]).join(' ')}`.toLowerCase();
    return hay.includes(label.toLowerCase());
  }

  function applyFilters() {
    const q = (searchInput?.value || '').toLowerCase().trim();
    const orgSlug = orgSelect?.value || '';
    const orgs = detectOrgs(DATA);
    console.log(`[Bundle] Filtering by org: ${orgSlug || 'All'} search: "${q}"`);
    const afterOrg = DATA.filter(p => productMatchesOrg(p, orgSlug, orgs));
    console.log(`[Bundle] After org filter: ${afterOrg.length}`);
    FILTERED = afterOrg.filter(p => {
      if (!q) return true;
      const hay = `${p.title||''} ${(p.tags||[]).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
    console.log(`[Bundle] Final products to render: ${FILTERED.length}`);
    render(FILTERED);
  }

  function populateOrgDropdown() {
    const STATIC_ORGS = [
      "Alpha Chi Omega","Alpha Delta Pi","Alpha Gamma Rho","Alpha Omicron Pi","Alpha Phi","Alpha Tau Omega",
      "Beta Theta Pi","Chi Omega","Chi Phi","Delta Chi","Delta Delta Delta","Delta Gamma","Gamma Phi Beta",
      "Kappa Alpha Order","Kappa Alpha Theta","Kappa Delta","Kappa Delta Rho","Kappa Kappa Gamma","Kappa Sigma",
      "Lambda Chi Alpha","Phi Delta Theta","Phi Gamma Delta","Phi Mu","Pi Beta Phi","Pi Kappa Alpha",
      "Sigma Alpha Epsilon","Sigma Chi","Sigma Kappa","Sigma Nu","Sigma Phi Epsilon","Sigma Sigma Sigma",
      "Theta Xi","Zeta Psi","Zeta Tau Alpha","Non-Branded"
    ];

    const orgs = STATIC_ORGS
      .filter(name => name !== 'Non-Branded')
      .map(slugify)
      .sort();

    const options = [
      `<option value="">All</option>`,
      `<option value="__non_branded__">Non-Branded</option>`,
      ...orgs.map(s => `<option value="${s}">${titleCaseFromSlug(s)}</option>`)
    ];

    orgSelect.innerHTML = options.join('');

    const params = new URLSearchParams(location.search);
    const raw = params.get('org') || '';
    if (raw) {
      const dec = multiDecode(raw);
      const slug = slugify(dec);
      const exists = orgs.includes(slug) || slug === 'non-branded';
      if (exists) orgSelect.value = slug === 'non-branded' ? '__non_branded__' : slug;
    }

    orgSelect.addEventListener('change', () => {
      const params = new URLSearchParams(location.search);
      if (orgSelect.value) {
        const label = orgSelect.value === '__non_branded__' ? 'Non-Branded' : titleCaseFromSlug(orgSelect.value);
        params.set('org', encodeURIComponent(label));
      } else {
        params.delete('org');
      }
      const qs = params.toString();
      history.replaceState(null, '', `${location.pathname}${qs ? `?${qs}` : ''}`);
      applyFilters();
    });
  }

  function updateSummaryFromBundle() {
    const items = Object.entries(bundle);
    const qty = items.reduce((s,[,i])=>s+i.quantity,0);
    const full = items.reduce((s,[,i])=>s+i.price*i.quantity,0);
    const rate = tier(qty);
    const discounted = full*(1-rate);
    const saved = full-discounted;

    if (subtotalEl) subtotalEl.textContent = fmt(discounted);
    if (savingsEl) {
      if (saved > 0) {
        savingsEl.textContent = `You saved ${fmt(saved)} (${(rate*100).toFixed(0)}% off ${qty} item${qty===1?'':'s'})`;
        savingsEl.hidden = false;
      } else {
        savingsEl.hidden = true;
        savingsEl.textContent = '';
      }
    }

    const existingSummaryList = document.getElementById('gr-summary-list');
    if (existingSummaryList) existingSummaryList.remove();

    if (items.length > 0) {
      const list = document.createElement('ul');
      list.id = 'gr-summary-list';
      list.style.marginTop = '6px';
      list.style.fontSize = '0.9rem';
      list.style.color = '#374151';
      list.style.listStyle = 'none';
      list.style.padding = '0';

      items.forEach(([id, item]) => {
        const li = document.createElement('li');
        li.innerHTML = `
          <div class="gr-summary-item">
            <strong>${item.title}</strong> — ${item.variantTitle || ''} — ${item.quantity} × ${fmt(item.price)}
          </div>
        `;
        list.appendChild(li);
      });
      savingsEl.insertAdjacentElement('afterend', list);
    }
  }

  function bindCheckout() {
    checkoutBtn?.addEventListener('click', async () => {
      const items = Object.entries(bundle)
        .filter(([,v])=>v && v.quantity>0)
        .map(([id,v])=>({ id, quantity:v.quantity }));

      if (!items.length) { alert('Please add at least one item to your bundle.'); return; }

      const qty = items.reduce((s,i)=>s+i.quantity,0);

      // ✅ FIXED CODE SELECTION (matches tier rules)
      const code = qty >= 5 ? 'BUNDLE15' : qty >= 3 ? 'BUNDLE10' : qty >= 2 ? 'BUNDLE5' : '';

      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type':'application/json' },
          body: JSON.stringify({ items })
        });
        if (code && hasDiscountFlag()) {
          window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/cart`;
        } else {
          window.location.href = '/cart';
          if (code && !hasDiscountFlag()) {
            alert('Bundle savings apply when you build your bundle on this page. Add items here, then checkout.');
          }
        }
      } catch (e) {
        console.error('[Bundle] Checkout error', e);
        window.location.href = code ? `/discount/${encodeURIComponent(code)}?redirect=/cart` : '/cart';
      }
    });
  }

  function normalizeProduct(p) {
    p.tags = Array.isArray(p.tags) ? p.tags : [];
    p.url = p.url || `/products/${p.handle}`;
    if (Array.isArray(p.variants)) {
      p.variants = p.variants.map(v => ({ ...v, price: priceToDollars(v.price) }));
    } else {
      p.variants = [];
    }
    return p;
  }

  async function boot() {
    injectBundlerStyles();

    let cached = loadCache();
    if (cached) {
      DATA = cached.map(normalizeProduct);
      console.log(`[Bundle] DATA size: ${DATA.length} (cache)`);
    } else {
      console.log('[Bundle] Fetching all bundle-eligible products…');
      const all = await loadAllPages();
      DATA = all.map(normalizeProduct);
      console.log(`[Bundle] DATA size: ${DATA.length} (fresh)`);
      saveCache(all);
    }

    populateOrgDropdown();

    if (searchInput) {
      searchInput.addEventListener('input', () => applyFilters());
    }

    applyFilters();
    updateSummaryFromBundle();

    const savedBundle = loadBundleState();
    if (savedBundle && Object.keys(savedBundle).length) {
      Object.assign(bundle, savedBundle);
      console.log(`[Bundle] Restored ${Object.keys(savedBundle).length} saved items`);
      updateSummaryFromBundle();
    }

    bindCheckout();

    const obs = new MutationObserver(updateSummaryFromBundle);
    obs.observe(grid, { childList:true, subtree:true });
  }

  document.addEventListener('DOMContentLoaded', boot);
})();